import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  Box, VStack, Heading, Text, Button, Container, Card, HStack, Badge, Separator, Avatar,
  Icon, Dialog, Field, Input, Textarea, Spinner
} from '@chakra-ui/react'
import { FaCheckCircle, FaMapMarkerAlt, FaEnvelope, FaCalendarAlt, FaGlobe } from 'react-icons/fa'
import { apiConfig } from '../config/api'
import { SpotlightHeader } from '../components/Spotlight/SpotlightHeader'
import { SpotlightFooter } from '../components/Spotlight/SpotlightFooter'

interface SpotlightPageData {
  slug: string
  name: string
  spotlight_tagline?: string
  spotlight_about?: string
  profile_photo_url?: string
  specialties?: string[]
  location?: string
  service_mode?: 'online_only' | 'in_person' | 'hybrid'
  languages?: string[]
  cta_text?: string
  cta_url?: string
  is_visible: boolean
}

export const SpotlightPage = () => {
  const { t } = useTranslation()
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  
  const [pageData, setPageData] = useState<SpotlightPageData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  
  // Message Dialog State
  const [messageOpen, setMessageOpen] = useState(false)
  const [msgName, setMsgName] = useState('')
  const [msgEmail, setMsgEmail] = useState('')
  const [msgBody, setMsgBody] = useState('')
  const [sending, setSending] = useState(false)
  const [msgSent, setMsgSent] = useState(false)

  useEffect(() => {
    if (!slug) return
    
    const fetchSpotlightPage = async () => {
      try {
        const res = await fetch(apiConfig.endpoints.spotlight(slug))
        if (!res.ok) {
          if (res.status === 404) {
            throw new Error("Spotlight page not found")
          }
          throw new Error("Failed to load spotlight page")
        }
        const data = await res.json()
        setPageData(data)
        setLoading(false)
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load spotlight page")
        setLoading(false)
      }
    }
    fetchSpotlightPage()
  }, [slug])
  
  const handleCTA = () => {
    if (pageData?.cta_url) {
      window.open(pageData.cta_url, '_blank')
    } else {
      // Default action - scroll to contact section
      window.location.href = '#contact'
    }
  }

  const handleSendMessage = async () => {
    if (!msgName || !msgEmail || !msgBody || !pageData) return
    setSending(true)
    try {
      // TODO: Implement feedback submission endpoint
      await new Promise(r => setTimeout(r, 1000))
      setMsgSent(true)
    } catch (e) {
      console.error(e)
    } finally {
      setSending(false)
    }
  }

  if (loading) {
    return (
      <Box minH="100vh" bg="white" display="flex" flexDirection="column">
        <SpotlightHeader />
        <Box flex={1} display="flex" alignItems="center" justifyContent="center">
          <VStack gap={4}>
            <Spinner size="xl" color="brand.500" />
            <Text color="gray.600">{t('spotlight.loading_page')}</Text>
          </VStack>
        </Box>
        <SpotlightFooter />
      </Box>
    )
  }

  if (error || !pageData) {
    return (
      <Box minH="100vh" bg="white" display="flex" flexDirection="column">
        <SpotlightHeader />
        <Box flex={1} display="flex" alignItems="center" justifyContent="center">
          <Container maxW="container.lg" py={12}>
            <VStack gap={4} textAlign="center">
              <Heading size="lg">{t('spotlight.page_not_found')}</Heading>
              <Text color="gray.600">{error || t('spotlight.page_not_found_description')}</Text>
              <Button onClick={() => navigate('/directory')}>{t('spotlight.back_to_directory')}</Button>
            </VStack>
          </Container>
        </Box>
        <SpotlightFooter />
      </Box>
    )
  }

  const tagline = pageData.spotlight_tagline || pageData.specialties?.[0] || ''
  const about = pageData.spotlight_about || ''

  return (
    <Box minH="100vh" bg="white" display="flex" flexDirection="column">
      <SpotlightHeader />
      <Box flex={1}>
        <Container maxW="container.lg" py={12} pb={{ base: 24, md: 12 }}>
        <VStack gap={8} align="stretch">
          {/* Header */}
          <Box textAlign="center" py={8}>
            <HStack justify="center" mb={4} flexWrap="wrap" gap={2}>
              <Badge colorPalette="purple" fontSize="sm" px={2}>
                {t('spotlight.powered_by')}
              </Badge>
              <Badge colorPalette="green" variant="solid" display="flex" alignItems="center" gap={1}>
                <Icon as={FaCheckCircle} /> {t('spotlight.verified_pro')}
              </Badge>
            </HStack>
            <Text fontSize="sm" color="fg.muted" mb={4}>
              {t('spotlight.landing_description')}
            </Text>
            
            <Avatar.Root size="2xl" mx="auto" mb={6} border="4px solid white" shadow="lg">
              <Avatar.Image src={pageData.profile_photo_url} />
              <Avatar.Fallback name={pageData.name} />
            </Avatar.Root>
            
            {/* H1: Practitioner name + specialty (outcome-focused) */}
            <Heading size="3xl" mb={3} px={4} lineHeight="tight">
              Work with {pageData.name}
            </Heading>
            
            {/* Subhead: Specialty + tagline */}
            <Heading size="md" color="fg.muted" fontWeight="medium" mb={2}>
              {pageData.specialties?.[0] || 'Wellness Practitioner'}
              {tagline && ` • ${tagline}`}
            </Heading>
            
            {/* Location / Service Mode */}
            <HStack justify="center" color="fg.muted" fontSize="sm" mb={2} wrap="wrap" gap={4}>
              {pageData.location ? (
                <HStack>
                  <Icon as={FaMapMarkerAlt} />
                  <Text>{pageData.location}</Text>
                </HStack>
              ) : pageData.service_mode === 'online_only' ? (
                <HStack>
                  <Icon as={FaGlobe} />
                  <Text>{t('spotlight.remote_online')}</Text>
                </HStack>
              ) : null}

              {/* Languages */}
              {pageData.languages && pageData.languages.length > 0 && (
                <HStack>
                  <Text color="gray.400">•</Text>
                  <Text>{t('spotlight.speaks')}: {pageData.languages.join(', ')}</Text>
                </HStack>
              )}
            </HStack>

            {about && <Text maxW="2xl" mx="auto" mt={4} color="fg.muted" fontSize="lg">{about}</Text>}
            
            <HStack justify="center" gap={4} mt={8} display={{ base: 'none', md: 'flex' }}>
              <Button colorPalette="blue" size="xl" onClick={handleCTA}>
                <Icon as={FaCalendarAlt} mr={2} /> {pageData.cta_text || t('spotlight.book_discovery_call')}
              </Button>
            </HStack>
            {pageData.cta_url && (
              <Text fontSize="sm" color="fg.muted" textAlign="center" mt={2}>
                You'll be redirected to schedule a time
              </Text>
            )}
          </Box>

          <Separator />

          {/* Specialties */}
          {pageData.specialties && pageData.specialties.length > 0 && (
            <Box>
              <Heading size="md" mb={4}>{t('spotlight.specialties')}</Heading>
              <HStack gap={2} wrap="wrap">
                {pageData.specialties.map((specialty, idx) => (
                  <Badge key={idx} colorPalette="blue" variant="subtle" px={3} py={1}>
                    {specialty}
                  </Badge>
                ))}
              </HStack>
            </Box>
          )}

          {/* Footer CTA */}
          <Box id="contact" bg="blue.50" p={8} borderRadius="2xl" textAlign="center" mt={8} border="1px dashed" borderColor="blue.200">
            <Heading size="lg" mb={4} color="blue.800">{t('spotlight.ready_to_start')}</Heading>
            <Text mb={6} color="blue.600" fontSize="lg">{t('spotlight.connect_with', { name: pageData.name })}</Text>
            {pageData.cta_url ? (
              <VStack gap={3}>
                <Button colorPalette="blue" size="xl" px={12} onClick={handleCTA}>
                  {pageData.cta_text || t('spotlight.work_with', { name: pageData.name.split(' ')[0] })}
                </Button>
                <Text fontSize="sm" color="blue.700">
                  You'll be redirected to schedule a time
                </Text>
              </VStack>
            ) : (
              <VStack gap={4} align="stretch" maxW="md" mx="auto">
                <Button colorPalette="blue" size="xl" px={12} onClick={() => setMessageOpen(true)}>
                  {pageData.cta_text || 'Ask a Question'}
                </Button>
                <Text fontSize="sm" color="blue.700">
                  We'll respond within 24 hours
                </Text>
              </VStack>
            )}
          </Box>

          {/* Message Dialog */}
          <Dialog.Root open={messageOpen} onOpenChange={(e) => setMessageOpen(e.open)}>
            <Dialog.Backdrop />
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header>
                  <Dialog.Title>Send a Message</Dialog.Title>
                </Dialog.Header>
                <Dialog.Body>
                  <VStack gap={4}>
                    <Field>
                      <Field.Label>Name</Field.Label>
                      <Input value={msgName} onChange={(e) => setMsgName(e.target.value)} placeholder="Your name" />
                    </Field>
                    <Field>
                      <Field.Label>Email</Field.Label>
                      <Input type="email" value={msgEmail} onChange={(e) => setMsgEmail(e.target.value)} placeholder="your@email.com" />
                    </Field>
                    <Field>
                      <Field.Label>Message</Field.Label>
                      <Textarea value={msgBody} onChange={(e) => setMsgBody(e.target.value)} placeholder="How can we help?" rows={4} />
                    </Field>
                  </VStack>
                </Dialog.Body>
                <Dialog.Footer>
                  <Button variant="ghost" onClick={() => setMessageOpen(false)}>Cancel</Button>
                  <Button 
                    colorPalette="blue" 
                    onClick={handleSendMessage} 
                    disabled={!msgName || !msgEmail || !msgBody || sending}
                  >
                    {sending ? 'Sending...' : msgSent ? 'Sent!' : 'Send Message'}
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Root>

          {/* Legal Disclaimer - Moved to less prominent position */}
          <Box 
            bg="yellow.50" 
            border="1px solid" 
            borderColor="yellow.200" 
            borderRadius="lg" 
            p={4} 
            mt={8}
          >
            <Text fontSize="xs" color="yellow.900" lineHeight="tall" textAlign="center">
              {t('spotlight.legal_notice_text')}
            </Text>
          </Box>
        </VStack>

        {/* Sticky Mobile CTA */}
        <Box 
          position="fixed" 
          bottom={0} 
          left={0} 
          right={0} 
          bg="white" 
          p={4} 
          borderTopWidth="1px" 
          shadow="xl" 
          display={{ base: 'block', md: 'none' }}
          zIndex={1000}
        >
          <Button w="full" colorPalette="blue" size="lg" onClick={handleCTA}>
            {pageData.cta_text || t('spotlight.book_call')}
          </Button>
        </Box>
        </Container>
      </Box>
      <SpotlightFooter />
    </Box>
  )
}
